import { CellProps } from 'react-table';
import CurrentStatusDot from '../../components/Other/CurrentStatusDot/CurrentStatusDot';
import Hint from '../../components/Other/Hint/Hint';
import CustomCell from '../../components/Table/Cell/CustomCell/CustomCell';
import {
  getDialerDetailsPath,
  getDIDDetailsPath,
  getEquipmentGroupDetailsPath,
  getOrganizationListPath,
  getPowerCyclesDetailsPath,
  getPRIDetailsPath,
  getPRIDeviceDetailsPath,
  getRouterDetailsPath,
  getSimsDetailsPath,
} from '../../routes/Router';
import { TcServiceTypeCategory } from '../../types/enums/tc-service-type-category.enum';
import { ColumnType } from '../../types/other';
import { RevioPackage } from '../../types/service/revio-package.type';
import { ServiceAssociatedItems } from '../../types/service/service-associated-items.type';
import { ServiceListItemType } from '../../types/service/ServiceListItemType';
import { ServiceTypeListItemType } from '../../types/service/type/ServiceTypeListItemType';
import { RevioFieldIdListItemType } from '../../types/tc-service/tc-service-type/revio-field-id/revio-field-id.type';
import { CURRENT_STATUS } from '../../utils/constants';
import { parsePhoneNumberWithDialCode } from '../../utils/utilities';
import { RevIoFieldIdPermissions } from '../../views/Main/RevIoFieldIds/RevIoFieldId.permissions';
import { getAllPermissionRolesForUserLevel, PERMISSIONROLECONSTANTS } from '../utils/utils.action';
import { formatDateTime } from '../../utils/dateUtils';

export const getAssociatedItem = ({
  dialer,
  did,
  equipment_group_service,
  sim,
  power_cycle,
  router,
  router_status,
  pri,
  pri_port,
}: ServiceAssociatedItems) => {
  if (dialer)
    return {
      href: getDialerDetailsPath.replace(':id', String(dialer.id)),
      label: dialer.radio_id,
      type: 'Dialer',
    };
  if (pri)
    return {
      href: getPRIDetailsPath.replace(':id', String(pri.id)),
      label: pri.description,
      type: 'PRI',
    };
  if (did)
    return {
      href: getDIDDetailsPath.replace(':id', String(did.id)),
      label: parsePhoneNumberWithDialCode(did.did, did.country?.code2 || 'US'),
      type: 'DID',
    };
  if (equipment_group_service)
    return {
      href: getEquipmentGroupDetailsPath.replace(':id', String(equipment_group_service.id)),
      label: `(${equipment_group_service.id}) ${equipment_group_service.description}`,
      type: 'Equipment Group',
    };
  if (sim)
    return {
      href: getSimsDetailsPath.replace(':id', String(sim.id)),
      label: sim.eid,
      type: 'SIM',
    };
  if (power_cycle)
    return {
      href: getPowerCyclesDetailsPath.replace(':id', String(power_cycle.id)),
      label: power_cycle.sn,
      type: 'Power Cycle',
    };
  if (router)
    return {
      href: getRouterDetailsPath.replace(':id', String(router.id)),
      label: router.serial_number,
      type: 'Router',
    };
  if (router_status)
    return {
      href: getRouterDetailsPath.replace(':id', String(router_status.id)),
      label: router_status.serial_number,
      type: 'Router',
    };
  if (pri_port)
    return {
      href: getPRIDeviceDetailsPath.replace(':id', String(pri_port.priDevice.id)),
      label: `${pri_port.priDevice.serialNumber}_${pri_port.fxsPort}`,
      type: 'PRI Device Port',
    };
  return null;
};

export const serviceCategoryLabel = (category?: TcServiceTypeCategory) => {
  switch (category) {
    case TcServiceTypeCategory.TC_SERVICE_UNDEFINED:
      return 'Not Defined';
    case TcServiceTypeCategory.TC_SERVICE_ACCOUNT_KEEP_ALIVE:
      return 'Account Keep Alive';
    case TcServiceTypeCategory.TC_SERVICE_CPAAS_DEFAULT:
      return 'CPaaS Default';
    case TcServiceTypeCategory.TC_SERVICE_CPAAS_BOBO_DEFAULT:
      return 'CPaaS BoBo Default';
    case TcServiceTypeCategory.TC_SERVICE_DID_LIC:
      return 'DID LIC';
    case TcServiceTypeCategory.TC_SERVICE_DID_SEAT:
      return 'DID Seat';
    case TcServiceTypeCategory.TC_SERVICE_SIM:
      return 'SIM Data Plan';
    case TcServiceTypeCategory.TC_SERVICE_DIALER:
      return 'Dialer';
    case TcServiceTypeCategory.TC_SERVICE_POWERCYCLE:
      return 'Power Cycle';
    case TcServiceTypeCategory.TC_SERVICE_HAAS:
      return 'HaaS';
    case TcServiceTypeCategory.TC_SERVICE_ROUTER_STATUS:
      return 'Router Status';
    case TcServiceTypeCategory.TC_SERVICE_ROUTER:
      return 'Router';
    case TcServiceTypeCategory.TC_SERVICE_FIBER_CIRCUIT:
      return 'Fiber Circuit';
    case TcServiceTypeCategory.TC_SERVICE_STARLINK_CIRCUIT:
      return 'Starlink Circuit';
    case TcServiceTypeCategory.TC_SERVICE_MONITOR:
      return 'Monitor';
    case TcServiceTypeCategory.TC_SERVICE_PBX:
      return 'PBX';
    case TcServiceTypeCategory.TC_SERVICE_TELCO:
      return 'TELCO';
    case TcServiceTypeCategory.TC_SERVICE_ATA_PORT:
      return 'ATA Port';
    default:
      return '';
  }
};

export const formatPackagePrice = (revioPackage?: RevioPackage | null) => {
  if (!revioPackage || typeof revioPackage.price !== 'number') return '';

  if (!revioPackage.priceCurrencyCode) return revioPackage.price.toFixed(2);

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: revioPackage.priceCurrencyCode,
  }).format(revioPackage.price);
};

export const tcServiceTypeRevIoFieldsColumns: Array<ColumnType<RevioFieldIdListItemType>> = [
  {
    Header: 'Rev.io Service Type ID',
    accessor: 'revioServiceTypeId',
    csvKey: 'revioServiceTypeId',
    minCellWidth: '100px',
    filterKey: 'revio_field_id.revioServiceTypeId',
  },
  {
    Header: 'Field Type',
    accessor: 'fieldType',
    csvKey: 'fieldType',
    minCellWidth: '100px',
    filterKey: 'revio_field_id.fieldType',
  },
  {
    Header: 'Field ID',
    accessor: 'fieldId',
    csvKey: 'fieldId',
    minCellWidth: '100px',
    filterKey: 'revio_field_id.fieldId',
  },
  {
    Header: 'Is BoBo',
    accessor: 'isBobo',
    csvKey: 'isBobo',
    minCellWidth: '100px',
    filterKey: 'revio_field_id.isBobo',
    Cell: (value: CellProps<RevioFieldIdListItemType>) => {
      return <CustomCell value={value.row.original.isBobo} type='boolean' />;
    },
  },
  {
    Header: ' ',
    minCellWidth: '50px',
    allowedRoles: RevIoFieldIdPermissions.DELETE,
    Cell: () => {
      return <CustomCell type='remove' />;
    },
  },
];

export const serviceListColumns: Array<ColumnType<ServiceListItemType>> = [
  {
    Header: 'ID',
    accessor: 'id',
    csvKey: 'id',
    filterKey: 'tc_service.id',
    minCellWidth: '70px',
  },
  {
    Header: (
      <>
        Rev.io <br /> ID
      </>
    ),
    name: 'Rev.io ID',
    id: 'revioId',
    accessor: 'revio_service_id',
    csvKey: 'revio_service_id',
    filterKey: 'revio_service_id',
    allowedRoles: [
      PERMISSIONROLECONSTANTS.superadmin,
      ...getAllPermissionRolesForUserLevel('telcloud', ['admin', 't3_admin']),
    ],
    minCellWidth: '70px',
  },
  {
    Header: (
      <>
        BoBo <br /> Rev.io <br /> ID
      </>
    ),
    name: 'BoBo Rev.io ID',
    id: 'boboRevioId',
    accessor: 'revio_bobo_service_id',
    csvKey: 'revio_bobo_service_id',
    filterKey: 'revio_bobo_service_id',
    allowedRoles: [
      PERMISSIONROLECONSTANTS.superadmin,
      ...getAllPermissionRolesForUserLevel('telcloud', ['admin', 't3_admin']),
    ],
    minCellWidth: '70px',
  },
  {
    Header: 'Type',
    accessor: row => row.service_type?.name,
    csvKey: 'service_type.name',
    filterKey: 'service_type.name',
  },
  {
    Header: 'Is Test',
    csvKey: 'isTest',
    csvTransform: (row: ServiceListItemType) => (row.organization?.isTest ? 'Yes' : 'No'),
    minCellWidth: '90px',
    allowedRoles: [
      PERMISSIONROLECONSTANTS.superadmin,
      ...getAllPermissionRolesForUserLevel('telcloud', ['admin', 't3_admin', 'support']),
    ],
    Cell: (value: CellProps<ServiceListItemType>) => {
      return <CustomCell value={value.row.original.organization?.isTest} type={'boolean'} />;
    },
  },
  {
    Header: 'Name',
    accessor: 'name',
    csvKey: 'name',
    Cell: (value: CellProps<ServiceListItemType>) => {
      return (
        <CustomCell
          warnings={[
            {
              message: 'Service without revio service id',
              show: !value.row.original?.revio_service_id,
            },
            {
              message: 'Service without bobo service id',
              show:
                !value.row.original?.revio_bobo_service_id && value.row.original?.organization?.parent?.bobo_enabled,
            },
          ]}
          showFix={
            !value.row.original?.revio_service_id ||
            (!value.row.original?.revio_bobo_service_id && value.row.original?.organization?.parent?.bobo_enabled)
          }
          value={value.value}
          type='warningName'
        />
      );
    },
    filterKey: 'tc_service.name',
  },
  {
    Header: 'Category',
    accessor: row => serviceCategoryLabel(row.service_type?.category),
    csvKey: 'service_type.category',
    filterKey: 'service_type.category',
  },
  {
    Header: 'Associated Item',
    csvKey: 'associatedItem',
    csvTransform: (row: ServiceListItemType) => {
      if (row.service_type?.category === TcServiceTypeCategory.TC_SERVICE_ACCOUNT_KEEP_ALIVE) {
        return row.organization?.name || '';
      }

      const item = getAssociatedItem(row);
      return item ? `${item.label} (${item.type})` : '';
    },
    filterKey: 'associated_item',
    Cell: ({ row }: CellProps<ServiceListItemType>) => {
      if (row.original.service_type?.category === TcServiceTypeCategory.TC_SERVICE_ACCOUNT_KEEP_ALIVE) {
        const organization = row.original.organization;
        if (!organization) return '';

        const link = `${getOrganizationListPath}/${organization?.id}`;
        return <CustomCell type='link' value={organization.name} link={link} />;
      }

      const item = getAssociatedItem(row.original);

      if (item) return <CustomCell type='link' value={item.label} link={item.href} valueType={`(${item.type})`} />;

      if (
        row.original.service_type?.category === TcServiceTypeCategory.TC_SERVICE_CPAAS_DEFAULT ||
        row.original.service_type?.category === TcServiceTypeCategory.TC_SERVICE_CPAAS_BOBO_DEFAULT
      )
        return <></>;

      return (
        <Hint icon='warning' title='This service is missing an associated item!' className='d-block text-center' />
      );
    },
  },
  {
    Header: 'Price',
    id: 'price',
    csvKey: 'price',
    csvTransform: (row: ServiceListItemType) => formatPackagePrice(row.package),
    accessor: service => formatPackagePrice(service.package),
    filterKey: 'package.price',
    allowedRoles: [PERMISSIONROLECONSTANTS.superadmin, PERMISSIONROLECONSTANTS.telcloud_admin],
    minCellWidth: '80px',
    textAlign: 'right',
  },
  {
    Header: 'BoBo Price',
    id: 'boboPrice',
    csvKey: 'boboPrice',
    csvTransform: (row: ServiceListItemType) => formatPackagePrice(row.boboPackage),
    accessor: service => formatPackagePrice(service.boboPackage),
    filterKey: 'boboPackage.price',
    allowedRoles: [PERMISSIONROLECONSTANTS.superadmin, PERMISSIONROLECONSTANTS.telcloud_admin],
    minCellWidth: '80px',
    textAlign: 'right',
  },
  {
    Header: 'Service Start',
    accessor: 'activated_at',
    csvKey: 'activated_at',
    filterKey: 'activated_at',
    Cell: ({ value, row }: CellProps<ServiceListItemType, string | null>) => {
      const isActive = row.original.is_active;
      return (
        <div className='d-flex align-items-center gap-1'>
          <CurrentStatusDot
            tooltipTitle={isActive ? 'Active' : 'Inactive'}
            value={isActive ? CURRENT_STATUS.ONLINE : CURRENT_STATUS.OFFLINE}
          />
          <div>{value ? formatDateTime(value) : ''}</div>
        </div>
      );
    },
  },
  {
    Header: 'Service Ended',
    accessor: 'disconnected_at',
    csvKey: 'disconnected_at',
    filterKey: 'disconnected_at',
    Cell: ({ value }: CellProps<ServiceListItemType, string | null>) => (
      <CustomCell value={value ? formatDateTime(value) : ''} />
    ),
  },
  {
    Header: 'Organization',
    accessor: 'organization',
    csvKey: 'organization.name',
    filterKey: 'organization.name',
    Cell: (value: CellProps<ServiceListItemType>) => {
      const org = value.cell.row.original.organization;
      return <CustomCell link={`/organizations/organizations/${org?.id}`} value={org?.name || ''} type='link' />;
    },
  },
  {
    Header: 'Location',
    accessor: 'location',
    csvKey: 'equipment_group.location.name',
    filterKey: 'location.name',
    Cell: (value: CellProps<ServiceListItemType>) => {
      const location = value.cell.row.original?.location;
      return (
        <CustomCell
          link={`/organizations/locations/${location?.id}`}
          value={location ? `(${location.id}) ${location.name}` : ''}
          type='link'
        />
      );
    },
  },
  {
    Header: 'Equipment Group',
    accessor: 'equipment_group',
    csvKey: 'equipment_group.description',
    filterKey: 'equipment_group.description',
    Cell: (value: CellProps<ServiceListItemType>) => {
      const equipmentGroup = value.cell.row.original.equipment_group ? value.row.values.equipment_group : '';
      return (
        <CustomCell
          link={`/equipments/groups/${equipmentGroup?.id}`}
          value={equipmentGroup?.description ? `(${equipmentGroup.id}) ${equipmentGroup.description}` : ''}
          type='link'
        />
      );
    },
  },
];

export const serviceTypeListColumns: Array<ColumnType<ServiceTypeListItemType>> = [
  {
    Header: 'ID',
    csvKey: 'id',
    minCellWidth: '20px',
    cellWidth: '100px',
    type: 'string',
    Cell: (value: CellProps<ServiceTypeListItemType>) => {
      return <CustomCell value={value.row.original.id} type='string' />;
    },
    filterKey: 'tc_service_type.id',
  },
  {
    Header: 'Name',
    csvKey: 'name',
    minCellWidth: '100px',
    type: 'string',
    Cell: (value: CellProps<ServiceTypeListItemType>) => {
      return <CustomCell value={value.row.original.name} type='string' />;
    },
    filterKey: 'tc_service_type.name',
  },
  {
    Header: 'Category',
    accessor: row => serviceCategoryLabel(row.category),
    csvKey: 'category',
    filterKey: 'tc_service_type.category',
    minCellWidth: '100px',
  },
  {
    Header: 'Service Type ID',
    csvKey: 'revio_service_id',
    minCellWidth: '100px',
    type: 'string',
    Cell: (value: CellProps<ServiceTypeListItemType>) => {
      return <CustomCell value={value.row.original.revio_service_id} type='string' />;
    },
    filterKey: 'tc_service_type.revio_service_id',
  },
  {
    Header: 'Provider ID',
    csvKey: 'revio_provider_id',
    minCellWidth: '100px',
    type: 'string',
    Cell: (value: CellProps<ServiceTypeListItemType>) => {
      return <CustomCell value={value.row.original.revio_provider_id} type='string' />;
    },
    filterKey: 'tc_service_type.revio_provider_id',
  },
  {
    Header: 'Usage Plan Group ID',
    csvKey: 'revio_usage_plan_group_id',
    minCellWidth: '100px',
    type: 'string',
    Cell: (value: CellProps<ServiceTypeListItemType>) => {
      return <CustomCell value={value.row.original.revio_usage_plan_group_id} type='string' />;
    },
    filterKey: 'tc_service_type.revio_usage_plan_group_id',
  },
  {
    Header: 'Package ID',
    csvKey: 'revio_package_id',
    minCellWidth: '100px',
    type: 'string',
    Cell: (value: CellProps<ServiceTypeListItemType>) => {
      return <CustomCell value={value.row.original.revio_package_id} type='string' />;
    },
    filterKey: 'tc_service_type.revio_package_id',
  },
];
